/**
 * Text - Typography Primitive Component
 *
 * Replaces raw <p>, <span>, <h1>-<h6> usage with a typed, styled component.
 * Provides consistent typography across the app.
 *
 * @example
 * <Text variant="h1">Page Title</Text>
 * <Text variant="body" color="secondary">Description text</Text>
 * <Text as="span" variant="caption">Small text</Text>
 */
import React from "react";
import { cn } from "@/shared/lib/utils/cn";
import type { PolymorphicProps } from "./types";

interface TextOwnProps {
  /** Typography variant determines the semantic element and styling */
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body"
    | "bodyLarge"
    | "caption"
    | "label";
  /** Text color variant */
  color?: "primary" | "secondary" | "muted" | "error" | "success";
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Font weight override */
  weight?: "normal" | "medium" | "semibold" | "bold";
  /** Additional CSS classes */
  className?: string;
  /** Children content */
  children?: React.ReactNode;
}

export type TextProps<E extends React.ElementType = "p"> = PolymorphicProps<
  E,
  TextOwnProps
>;

// Variant to element mapping (semantic HTML)
const variantElementMap = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body: "p",
  bodyLarge: "p",
  caption: "span",
  label: "label",
} as const;

// Variant styling classes
const variantClasses = {
  h1: "text-4xl sm:text-5xl lg:text-6xl font-bold",
  h2: "text-3xl sm:text-4xl font-bold",
  h3: "text-2xl sm:text-3xl font-bold",
  h4: "text-xl sm:text-2xl font-semibold",
  h5: "text-lg sm:text-xl font-semibold",
  h6: "text-base sm:text-lg font-semibold",
  body: "text-base",
  bodyLarge: "text-lg",
  caption: "text-sm",
  label: "text-sm font-medium",
} as const;

// Color classes
const colorClasses = {
  primary: "text-gray-900",
  secondary: "text-gray-600",
  muted: "text-gray-500",
  error: "text-red-600",
  success: "text-green-600",
} as const;

// Alignment classes
const alignClasses = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

// Weight classes
const weightClasses = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
} as const;

/**
 * Text component - replaces raw HTML text elements
 */
export function Text<E extends React.ElementType = "p">({
  as,
  variant = "body",
  color = "primary",
  align,
  weight,
  className,
  children,
  ...props
}: TextProps<E>) {
  // Determine element: custom "as" prop > variant's semantic element > default p
  const Component = as || variantElementMap[variant] || "p";

  // Build classes
  const classes = cn(
    variantClasses[variant],
    colorClasses[color],
    align && alignClasses[align],
    weight && weightClasses[weight],
    className,
  );

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  );
}

Text.displayName = "Text";
